import * as THREE from "three";
import { deinterleaveGeometry, mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

export interface BatchStats {
  meshesBefore: number;
  meshesAfter: number;
  batches: number;
}

function animatedNodes(root: THREE.Object3D, clips: THREE.AnimationClip[]): Set<THREE.Object3D> {
  const targets = new Set<THREE.Object3D>();
  for (const clip of clips) for (const track of clip.tracks) {
    const path = THREE.PropertyBinding.parseTrackName(track.name);
    const node = THREE.PropertyBinding.findNode(root, path.nodeName) as THREE.Object3D | null;
    if (!node) throw new Error(`Cannot preserve animation target ${track.name}`);
    targets.add(node);
    // Bone tracks can bind through a SkinnedMesh instead of naming the bone node.
    if (path.objectName === "bones" && (node as THREE.SkinnedMesh).isSkinnedMesh) {
      const bones = (node as THREE.SkinnedMesh).skeleton.bones;
      const bone = bones.find((b) => b.name === path.objectIndex) ?? bones[Number(path.objectIndex)];
      if (bone) targets.add(bone);
    }
  }
  return targets;
}

function deforms(node: THREE.Object3D): boolean {
  const mesh = node as THREE.Mesh;
  return (node as THREE.Bone).isBone === true || (mesh as THREE.SkinnedMesh).isSkinnedMesh === true ||
    (mesh.isMesh && Object.keys(mesh.geometry.morphAttributes).length > 0);
}

function geometryKey(geometry: THREE.BufferGeometry): string {
  return Object.entries(geometry.attributes).sort(([a], [b]) => a.localeCompare(b)).map(([name, attribute]) => {
    const array = attribute instanceof THREE.InterleavedBufferAttribute ? attribute.data.array : attribute.array;
    return `${name}:${attribute.itemSize}:${attribute.normalized}:${array.constructor.name}`;
  }).join("|");
}

function bakeGeometry(mesh: THREE.Mesh, anchor: THREE.Object3D): THREE.BufferGeometry {
  const geometry = mesh.geometry.clone();
  deinterleaveGeometry(geometry);
  const relative = anchor.matrixWorld.clone().invert().multiply(mesh.matrixWorld);
  geometry.applyMatrix4(relative);
  const position = geometry.getAttribute("position");
  if (!geometry.index) geometry.setIndex(Array.from({ length: position.count }, (_, index) => index));
  // Once reflection is baked into vertices it no longer flips the object's
  // renderer front-face state. Reverse winding and tangent handedness explicitly.
  if (relative.determinant() < 0) {
    const index = geometry.index!;
    for (let i = 0; i < index.count; i += 3) {
      const second = index.getX(i + 1);
      index.setX(i + 1, index.getX(i + 2));
      index.setX(i + 2, second);
    }
    const tangent = geometry.getAttribute("tangent");
    if (tangent) for (let i = 0; i < tangent.count; i++) tangent.setW(i, -tangent.getW(i));
  }
  geometry.clearGroups();
  return geometry;
}

/**
 * Reduces template draw calls once at load time. Every animation target/pivot
 * remains in its original hierarchy. Only compatible static leaves are replaced;
 * their geometry is expressed relative to the nearest animated ancestor.
 */
export function batchStaticMeshes(root: THREE.Object3D, clips: THREE.AnimationClip[] = []): BatchStats {
  root.updateWorldMatrix(true, true);
  const animated = animatedNodes(root, clips);
  const groups = new Map<THREE.Object3D, Map<string, THREE.Mesh[]>>();
  let meshesBefore = 0;
  root.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    meshesBefore++;
    if (mesh.children.length || animated.has(mesh) || deforms(mesh) || (mesh as THREE.InstancedMesh).isInstancedMesh ||
        Array.isArray(mesh.material) || mesh.material.transparent || !mesh.visible ||
        mesh.customDepthMaterial || mesh.customDistanceMaterial ||
        mesh.geometry.drawRange.start !== 0 || mesh.geometry.drawRange.count !== Infinity) return;
    if (!mesh.geometry.getAttribute("position")) return;
    let anchor = root;
    // Hidden parents and deforming subtrees stay completely intact. Render-order
    // groups are also boundaries: moving leaves across them changes sorting.
    for (let parent = mesh.parent; parent; parent = parent.parent) {
      if (!parent.visible || deforms(parent)) return;
      if (anchor === root && (animated.has(parent) || parent.renderOrder !== 0)) anchor = parent;
      if (parent === root) break;
    }
    const determinant = anchor.matrixWorld.determinant();
    if (!Number.isFinite(determinant) || Math.abs(determinant) < 1e-12) return;
    const key = [mesh.material.uuid, geometryKey(mesh.geometry), mesh.castShadow, mesh.receiveShadow, mesh.layers.mask, mesh.renderOrder, mesh.frustumCulled].join(";");
    const byMaterial = groups.get(anchor) ?? new Map<string, THREE.Mesh[]>();
    const leaves = byMaterial.get(key) ?? [];
    leaves.push(mesh); byMaterial.set(key, leaves); groups.set(anchor, byMaterial);
  });
  let batches = 0;
  let removed = 0;
  for (const [anchor, byMaterial] of groups) for (const leaves of byMaterial.values()) {
    if (leaves.length < 2) continue;
    const geometries = leaves.map((mesh) => bakeGeometry(mesh, anchor));
    const geometry = mergeGeometries(geometries, false);
    for (const part of geometries) part.dispose();
    if (!geometry) throw new Error(`Static batch has incompatible geometry under ${anchor.name}`);
    geometry.computeBoundingBox(); geometry.computeBoundingSphere();
    const first = leaves[0]!;
    const merged = new THREE.Mesh(geometry, first.material);
    merged.name = `static_batch_${batches}`;
    merged.castShadow = first.castShadow; merged.receiveShadow = first.receiveShadow;
    merged.layers.mask = first.layers.mask; merged.renderOrder = first.renderOrder; merged.frustumCulled = first.frustumCulled;
    merged.userData["batchedSources"] = leaves.map((mesh) => mesh.name);
    anchor.add(merged);
    for (const leaf of leaves) leaf.removeFromParent();
    removed += leaves.length - 1;
    batches++;
  }
  root.updateWorldMatrix(true, true);
  return { meshesBefore, meshesAfter: meshesBefore - removed, batches };
}
